import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { legalApi } from '../../api/client';
import { Button } from '../../components/ui/button';
import { usePageTitle } from '../../hooks/usePageTitle';
import { QuillEditor } from '../../components/ui/quill-editor';
import '../../styles/quill-dark.css';

const inputClass = 'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground outline-none focus:border-primary/50';
const CONTENT_MAX_LENGTH = 200000;

function getErrorMessage(error, fallback) {
  return error?.response?.data?.message || fallback;
}

export default function AdminLegalPagesPage() {
  usePageTitle('Legal Pages · Admin · Kast');
  const client = useQueryClient();
  const { data: pages = [], isLoading, isError, error } = useQuery({
    queryKey: ['admin-legal-pages'], queryFn: async () => (await legalApi.getForAdmin()).data.data,
  });
  const [selectedSlug, setSelectedSlug] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', isPublished: true });
  const selected = pages.find((page) => page.Slug === selectedSlug) || pages[0];

  useEffect(() => {
    if (selected) setForm({ title: selected.Title, content: selected.Content, isPublished: selected.IsPublished });
  }, [selected]);

  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['admin-legal-pages'] }),
      client.invalidateQueries({ queryKey: ['legal'] }),
    ]);
  };
  const save = useMutation({
    mutationFn: () => legalApi.update(selected.Slug, form),
    onSuccess: async () => {
      toast.success('Legal page saved.');
      await refresh();
    },
    onError: (saveError) => toast.error(getErrorMessage(saveError, 'Could not save this page.')),
  });
  const reset = useMutation({
    mutationFn: () => legalApi.reset(selected.Slug),
    onSuccess: async () => {
      toast.success('Restored the starter template.');
      await refresh();
    },
    onError: (resetError) => toast.error(getErrorMessage(resetError, 'Could not reset this page.')),
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading legal pages…</div>;
  if (isError) return <div role="alert" className="p-6 text-sm text-destructive">{getErrorMessage(error, 'Could not load legal pages.')}</div>;
  if (!selected) return <div className="p-6 text-sm text-muted-foreground">No legal pages found.</div>;

  return <div className="mx-auto max-w-5xl p-5 sm:p-7">
    <div className="mb-6"><h1 className="text-xl font-semibold">Legal Pages</h1><p className="mt-1 text-sm text-muted-foreground">Edit the Terms and Conditions and Privacy Policy shown on web and mobile. HTML is cleaned before it is saved.</p></div>
    <div className="grid gap-5 md:grid-cols-[220px_1fr]">
      <aside className="flex gap-2 md:flex-col">
        {pages.map((page) => <button key={page.Slug} onClick={() => setSelectedSlug(page.Slug)} className={`rounded-lg border px-3 py-3 text-left text-sm ${selected.Slug === page.Slug ? 'border-primary/50 bg-primary/10 text-foreground' : 'border-white/10 text-muted-foreground hover:bg-white/[0.03]'}`}>
          <span className="block font-medium">{page.Title}</span><span className="mt-1 block text-xs opacity-70">v{page.Version} · {page.IsPublished ? 'Published' : 'Draft'}</span>
        </button>)}
      </aside>
      <section className="rounded-xl bg-white/[0.03] p-5 ring-1 ring-white/10">
        <label htmlFor="legal-page-title" className="mb-1.5 block text-xs font-medium text-muted-foreground">Page title</label>
        <input id="legal-page-title" className={inputClass} value={form.title} maxLength={200} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
        <label className="mb-1.5 mt-5 block text-xs font-medium text-muted-foreground">Content</label>
        <QuillEditor
          key={selected.Slug}
          className="legal-quill"
          defaultValue={selected.Content}
          placeholder="Write the page content…"
          onChange={(html) => setForm((f) => ({ ...f, content: html }))}
        />
        <p className="mt-1 text-right text-xs text-muted-foreground">{form.content.length.toLocaleString()} / {CONTENT_MAX_LENGTH.toLocaleString()}</p>
        <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={form.isPublished} onChange={(e) => setForm((f) => ({ ...f, isPublished: e.target.checked }))} className="size-4 accent-primary" /> Published and visible to viewers</label>
        <div className="mt-5 flex flex-wrap justify-end gap-2"><Button variant="secondary" onClick={() => reset.mutate()} disabled={save.isPending || reset.isPending} className="gap-1.5"><Icon icon="ph:arrow-counter-clockwise-bold" /> {reset.isPending ? 'Restoring…' : 'Reset template'}</Button><Button onClick={() => save.mutate()} disabled={save.isPending || reset.isPending} className="gap-1.5"><Icon icon="ph:floppy-disk-bold" /> {save.isPending ? 'Saving…' : 'Save changes'}</Button></div>
      </section>
    </div>
  </div>;
}
